import { Injectable } from '@angular/core';
import { ApiBaseService } from './api-base.service';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GridConnectionService {

  currentSelectedProjectId: any = 0;

  currentSelectedProjectIdChange: Subject<any> = new Subject<any>();

  // State management for grid connections
  public gridConnections$ = new BehaviorSubject<any[]>([]);
  private isGridConnectionsLoading = false;

  // State management for grid connection locations
  public gridConnectionLocations$ = new BehaviorSubject<any[]>([]);
  private isGridConnectionLocationsLoading = false;

  constructor(private apiService: ApiBaseService) {

    this.currentSelectedProjectId = 0;
    this.currentSelectedProjectIdChange.subscribe((value) => {
      this.currentSelectedProjectId = value;
    });
  }

  updateProjectId(projectId: any) {
    this.currentSelectedProjectIdChange.next(projectId);
  }

  async getGridConnectionList(payLoad: any): Promise<any> {
    const result = await this.apiService.httpRequest('GridConnectionService/GetGridConnectionList', payLoad, true, true);

    // Update grid connections state if available
    if (!result.hasError && result.gridConnections) {
      this.gridConnections$.next(result.gridConnections);
    }

    return result;
  }

  async loadGridConnections(forceReload: boolean = false): Promise<any[]> {
    if (!forceReload && this.gridConnections$.value.length > 0) {
      return this.gridConnections$.value;
    }

    if (this.isGridConnectionsLoading) {
      return this.gridConnections$.value;
    }

    this.isGridConnectionsLoading = true;
    try {
      const result = await this.getGridConnectionList({});
      return this.gridConnections$.value;
    } finally {
      this.isGridConnectionsLoading = false;
    }
  }

  async getProjectsByGridConnectionId(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('GridConnectionService/GetProjectsByGridConnectionId', payLoad, true, true);
  }

  async getGridConnectionLocations(payLoad: any): Promise<any> {
    const result = await this.apiService.httpRequest('GridConnectionService/GetGridConnectionLocations', payLoad, true, true);

    // Update grid connection locations state if available
    if (!result.hasError && result.locations) {
      this.gridConnectionLocations$.next(result.locations);
    }

    return result;
  }

  async loadGridConnectionLocations(forceReload: boolean = false): Promise<any[]> {
    if (!forceReload && this.gridConnectionLocations$.value.length > 0) {
      return this.gridConnectionLocations$.value;
    }

    if (this.isGridConnectionLocationsLoading) {
      return this.gridConnectionLocations$.value;
    }

    this.isGridConnectionLocationsLoading = true;
    try {
      const result = await this.getGridConnectionLocations({});
      return this.gridConnectionLocations$.value;
    } finally {
      this.isGridConnectionLocationsLoading = false;
    }
  }

  async getGridConnectionLocationTypes(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('GridConnectionService/GetGridConnectionLocationTypes', payLoad, true, true);
  }

  async getPropertyList(payload: any): Promise<any> {
    return this.apiService.httpRequest('PropertyService/GetPropertyList', payload, true, true);
  }

  async getGridConnectionTitle(payload: any): Promise<any> {
    return this.apiService.httpRequest('GridConnectionService/GetGridConnectionTitle', payload, true, true);
  }

  async createNewGridConnection(payload: any): Promise<any> {
    return this.apiService.httpRequest('GridConnectionService/CreateNewGridConnection', payload, true, true);
  }

  async deleteGridConnectionLocation(payload: any): Promise<any> {
    return this.apiService.httpRequest('GridConnectionService/DeleteGridConnectionLocation', payload, true, true);
  }

  async getSiteOverview(payload: any): Promise<any> {
    return this.apiService.httpRequest('GridConnectionService/GetSiteOverview', payload, true, true);
  }

  async writeSiteOverview(payload: any): Promise<any> {
    return this.apiService.httpRequest('GridConnectionService/WriteSiteOverview', payload, true, true);
  }

  async saveGridConnectionLocation(payload: any): Promise<any> {
    return this.apiService.httpRequest('GridConnectionService/SaveGridConnectionLocation', payload, true, true);
  }

  async removeGridConnectionLocation(payload: any): Promise<any> {
    return this.apiService.httpRequest('GridConnectionService/RemoveGridConnectionLocation', payload, true, true);
  }

  async deleteGridConnection(payload: any): Promise<any> {
    return this.apiService.httpRequest('GridConnectionService/DeleteGridConnection', payload, true, true);
  }

  async deleteProject(payload: any): Promise<any> {
    return this.apiService.httpRequest('ProjectService/DeleteProject', payload, true, true);
  }

  async getCommentCount(payload: any): Promise<any> {
    return this.apiService.httpRequest('CommentService/GetCommentCount', payload, true, true);
  }

  async getCountries(payload: any): Promise<any> {
    return this.apiService.httpRequest('GridConnectionService/GetCountries', payload, true, true);
  }

  async getStates(payload: any): Promise<any> {
    return this.apiService.httpRequest('GridConnectionService/GetStates', payload, true, true);
  }

}
